/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Button,
  TextField,
  Typography,
} from '@material-ui/core';

import {
  Alert,
  Rating,
} from '@material-ui/lab';

import {
  addReview,
  selectGuest,
  selectIsPending,
} from '../../store/widgetSlice';

import {
  setMessage,
} from '../../store/authSlice';

import {
  isRequired,
  noSpecialCharacters,
  noSequentialNumbers,
  validateField,
  validateForm,
} from '../../helpers/validation';

import RadioGroupField from '../common/RadioGroupField';

const useStyles = makeStyles((theme) => ({
  form: {
    width: 750,
    maxWidth: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  heading: {
    fontWeight: 600,
    margin: theme.spacing(1, 0),
  },
  description: {
    fontSize: 12,
    margin: theme.spacing(1, 0, 2),
  },
  rating: {
    marginLeft: -2,
  },
  radioGroup: {
    marginBottom: theme.spacing(2),
  },
  radioHelperText: {
    marginTop: -theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  surveyTextField: {
    width: `calc(100% - ${theme.spacing(2)}px)`,
    marginTop: -theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  elementHidden: {
    display: 'none',
  },
}));

export default function WriteReviewForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const guest = useSelector(selectGuest);
  const isPending = useSelector(selectIsPending);

  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    submitterFirstName: '',
    submitterLastName: '',
    submitterCompanyName: '',
    rating: '4',
    comment: '',
    excellentGuest: null,
    chargeback: null,
    propertyDamage: null,
    petPolicyViolation: null,
    noisePolicyViolation: null,
    occupancyPolicyViolation: null,
    negativeReview: null,
    nonCompliance: null,
  });

  const formRules = {
    submitterFirstName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    submitterLastName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    submitterCompanyName: [isRequired, noSpecialCharacters, noSequentialNumbers(8)],
    rating: [isRequired],
    comment: [],
    commentNegativeReview: [],
    commentNoisePolicyViolation: [],
    commentNonCompliance: [],
    commentPropertyDamage: [],
    excellentGuest: [isRequired],
    chargeback: [isRequired],
    propertyDamage: [isRequired],
    petPolicyViolation: [isRequired],
    noisePolicyViolation: [isRequired],
    occupancyPolicyViolation: [isRequired],
    negativeReview: [isRequired],
    nonCompliance: [isRequired],
  };

  const handleChange = (event) => {
    setFormValues({ ...formValues, [event.target.name]: event.target.value });
  };

  const handleBlur = (event) => {
    validateField(formRules, formErrors, setFormErrors, event.target.name, event.target.value);
  };

  const attemptSubmitReview = (event) => {
    event.preventDefault();
    if (validateForm(formRules, formValues, formErrors, setFormErrors)) {
      dispatch(addReview({
        guestId: guest.id,
        submitterFirstName: formValues.submitterFirstName,
        submitterLastName: formValues.submitterLastName,
        submitterCompanyName: formValues.submitterCompanyName,
        rating: formValues.rating,
        comment: formValues.comment,
        commentNegativeReview: formValues.commentNegativeReview,
        commentNoisePolicyViolation: formValues.commentNoisePolicyViolation,
        commentNonCompliance: formValues.commentNonCompliance,
        commentPropertyDamage: formValues.commentPropertyDamage,
        excellentGuest: (formValues.excellentGuest === 'yes'),
        chargeback: (formValues.chargeback === 'yes'),
        propertyDamage: (formValues.propertyDamage === 'yes'),
        petPolicyViolation: (formValues.petPolicyViolation === 'yes'),
        noisePolicyViolation: (formValues.noisePolicyViolation === 'yes'),
        occupancyPolicyViolation: (formValues.occupancyPolicyViolation === 'yes'),
        negativeReview: (formValues.negativeReview === 'yes'),
        nonCompliance: (formValues.nonCompliance === 'yes'),
      }))
        .unwrap()
        .then(() => {
          dispatch(setMessage({ message: 'Review created', severity: 'success' }));
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Review could not be created', severity: 'error' }));
        });
    }
  };

  return (
    <form className={classes.form}>
      <Typography className={classes.heading} component="h3" variant="h6">
        Submitter information
      </Typography>
      <Grid container spacing={2}>
        <Grid align="left" item sm={6}>
          <TextField
            variant="outlined"
            margin="normal"
            id="review-firstname"
            label="First Name"
            name="submitterFirstName"
            autoComplete="given-name"
            value={formValues.submitterFirstName}
            error={!!formErrors.submitterFirstName}
            helperText={formErrors.submitterFirstName}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            required
          />
        </Grid>
        <Grid align="right" item sm={6}>
          <TextField
            variant="outlined"
            margin="normal"
            id="review-lastname"
            label="Last Name"
            name="submitterLastName"
            autoComplete="family-name"
            value={formValues.submitterLastName}
            error={!!formErrors.submitterLastName}
            helperText={formErrors.submitterLastName}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            required
          />
        </Grid>
      </Grid>
      <TextField
        variant="outlined"
        margin="normal"
        id="review-companyname"
        label="Company Name"
        name="submitterCompanyName"
        value={formValues.submitterCompanyName}
        error={!!formErrors.submitterCompanyName}
        helperText={formErrors.submitterCompanyName}
        onChange={handleChange}
        onBlur={handleBlur}
        fullWidth
        required
      />
      <Typography className={classes.heading} component="h3" variant="h6">
        Rating and review
      </Typography>
      <Typography className={classes.description} component="p" variant="body1">
        Do you have an overall rating of this guest?
      </Typography>
      <Rating
        id="review-rating"
        name="rating"
        size="large"
        className={classes.rating}
        value={formValues.rating}
        onChange={handleChange}
      />
      <Typography className={classes.heading} component="h3" variant="h6">
        Survey
      </Typography>
      <Grid container>
        <Grid align="left" item sm={6}>
          <RadioGroupField
            fieldName="excellentGuest"
            fieldLabel="Excellent Guest (EG)"
            fieldError={formErrors.excellentGuest}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.excellentGuest}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <RadioGroupField
            fieldName="chargeback"
            fieldLabel="Did guest initiate a chargeback?"
            fieldError={formErrors.chargeback}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.chargeback}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <RadioGroupField
            fieldName="propertyDamage"
            fieldLabel="Did guest cause damage?"
            fieldError={formErrors.propertyDamage}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.propertyDamage}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextField
            className={clsx(
              classes.surveyTextField,
              (!formValues.propertyDamage || formValues.propertyDamage === 'no')
              && classes.elementHidden,
            )}
            variant="outlined"
            margin="normal"
            id="review-text-property-damage"
            label="Property Damage Details"
            name="commentPropertyDamage"
            value={formValues.commentPropertyDamage}
            error={!!formErrors.commentPropertyDamage}
            helperText={formErrors.commentPropertyDamage}
            onChange={handleChange}
            onBlur={handleBlur}
            minRows={2}
            multiline
            fullWidth
          />
          <RadioGroupField
            fieldName="negativeReview"
            fieldLabel="Did guest post a negative review?"
            fieldError={formErrors.negativeReview}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.negativeReview}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextField
            className={clsx(
              classes.surveyTextField,
              (!formValues.negativeReview || formValues.negativeReview === 'no')
              && classes.elementHidden,
            )}
            variant="outlined"
            margin="normal"
            id="review-text-negative-review"
            label="Negative Review Details"
            name="commentNegativeReview"
            value={formValues.commentNegativeReview}
            error={!!formErrors.commentNegativeReview}
            helperText={formErrors.commentNegativeReview}
            onChange={handleChange}
            onBlur={handleBlur}
            minRows={2}
            multiline
            fullWidth
          />
        </Grid>
        <Grid align="left" item sm={6}>
          <RadioGroupField
            fieldName="petPolicyViolation"
            fieldLabel="Did guest violate pet policy"
            fieldError={formErrors.petPolicyViolation}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.petPolicyViolation}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <RadioGroupField
            fieldName="occupancyPolicyViolation"
            fieldLabel="Did guest violate occupancy policy?"
            fieldError={formErrors.occupancyPolicyViolation}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.occupancyPolicyViolation}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <RadioGroupField
            fieldName="noisePolicyViolation"
            fieldLabel="Did guest violate noise policy?"
            fieldError={formErrors.noisePolicyViolation}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.noisePolicyViolation}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextField
            className={clsx(
              classes.surveyTextField,
              (!formValues.noisePolicyViolation || formValues.noisePolicyViolation === 'no')
              && classes.elementHidden,
            )}
            variant="outlined"
            margin="normal"
            id="review-text-noise-policy-violation"
            label="Noise Policy Details"
            name="commentNoisePolicyViolation"
            value={formValues.commentNoisePolicyViolation}
            error={!!formErrors.commentNoisePolicyViolation}
            helperText={formErrors.commentNoisePolicyViolation}
            onChange={handleChange}
            onBlur={handleBlur}
            minRows={2}
            multiline
            fullWidth
          />
          <RadioGroupField
            fieldName="nonCompliance"
            fieldLabel="Did guest not comply with rules?"
            fieldError={formErrors.nonCompliance}
            fieldValues={[
              { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' },
            ]}
            selectedValue={formValues.nonCompliance}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <TextField
            className={clsx(
              classes.surveyTextField,
              (!formValues.nonCompliance || formValues.nonCompliance === 'no')
              && classes.elementHidden,
            )}
            variant="outlined"
            margin="normal"
            id="review-text-non-compliance"
            label="Non-Compliance Details"
            name="commentNonCompliance"
            value={formValues.commentNonCompliance}
            error={!!formErrors.commentNonCompliance}
            helperText={formErrors.commentNonCompliance}
            onChange={handleChange}
            onBlur={handleBlur}
            minRows={2}
            multiline
            fullWidth
          />
        </Grid>
      </Grid>
      <Alert severity="warning">
        Please take note that the guest is going to be notified of when a review
        is written to give them a chance to reply
      </Alert>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={attemptSubmitReview}
        disabled={isPending}
        fullWidth
      >
        Submit your review
      </Button>
    </form>
  );
}
